import { useCallback, useMemo, useState } from "react"
import { httpsCallable } from "firebase/functions"
import { WheelSpinResetAdminPanel } from "react-ui-components/components/admin/WheelSpinResetAdminPanel"
import { PopupService } from "react-ui-components/components/PopupService"

import { functions } from "../../firebase"

type ResetWheelSpinsRequest = {
  dayKey?: string
  scope: "all" | "user"
  userId?: string
}

type ResetWheelSpinsResponse = {
  dayKey: string
  deletedCount: number
  resetAt: string
  resetBy: string
  scope: "all" | "user"
  userId?: string
}

const resetWheelSpinsCallable = httpsCallable<ResetWheelSpinsRequest, ResetWheelSpinsResponse>(
  functions,
  "resetWheelSpins",
)

const getTodayDayKey = () => new Date().toISOString().slice(0, 10)

export const WheelResetPanel = () => {
  const [userId, setUserId] = useState("")
  const [isResettingAll, setIsResettingAll] = useState(false)
  const [isResettingUser, setIsResettingUser] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [successMessage, setSuccessMessage] = useState("")
  const dayKey = useMemo(() => getTodayDayKey(), [])

  const runReset = useCallback(
    async (request: ResetWheelSpinsRequest) => {
      setErrorMessage("")
      setSuccessMessage("")
      const response = await resetWheelSpinsCallable(request)
      const result = response.data
      const message =
        result.scope === "user"
          ? `Reset ${result.deletedCount} spin record(s) for user ${result.userId}.`
          : `Reset ${result.deletedCount} spin record(s). All affected users can spin again today.`
      setSuccessMessage(message)
      PopupService.pushPopup(message)
    },
    [],
  )

  const handleResetAll = useCallback(() => {
    PopupService.setConfirm(
      "Reset today's wheel spin records for all users? Users who already spun today will be able to spin again and receive another prize.",
      (answer) => {
        if (answer !== "OK") {
          return
        }

        void (async () => {
          setIsResettingAll(true)
          try {
            await runReset({ scope: "all", dayKey })
          } catch (error: any) {
            const message = error?.message || "Unable to reset wheel spins for all users."
            setErrorMessage(message)
            PopupService.pushPopup(message)
          } finally {
            setIsResettingAll(false)
          }
        })()
      },
    )
  }, [dayKey, runReset])

  const handleResetUser = useCallback(() => {
    const trimmedUserId = userId.trim()
    if (!trimmedUserId) {
      setErrorMessage("Enter a Firebase Auth user ID before resetting one user.")
      return
    }

    PopupService.setConfirm(
      `Reset today's wheel spin record for user ${trimmedUserId}? This user will be able to spin again and receive another prize.`,
      (answer) => {
        if (answer !== "OK") {
          return
        }

        void (async () => {
          setIsResettingUser(true)
          try {
            await runReset({ scope: "user", userId: trimmedUserId, dayKey })
          } catch (error: any) {
            const message = error?.message || "Unable to reset wheel spins for this user."
            setErrorMessage(message)
            PopupService.pushPopup(message)
          } finally {
            setIsResettingUser(false)
          }
        })()
      },
    )
  }, [dayKey, runReset, userId])

  return (
    <WheelSpinResetAdminPanel
      title="Wheel spin resets"
      description="Reset today's spin eligibility without changing prize configuration, inventory counters, or previously granted awards."
      dayKey={dayKey}
      dayKeyLabel="UTC day"
      resetAllTitle="Reset all users"
      resetAllDescription="Deletes today's wheel spin records so every affected user can spin again and win another prize."
      resetAllLabel="Reset all users"
      resettingAllLabel="Resetting all users..."
      resetUserTitle="Reset one user"
      resetUserDescription="Deletes today's wheel spin record for one Firebase Auth UID."
      userId={userId}
      userIdLabel="User ID"
      userIdPlaceholder="Firebase Auth UID"
      userIdHelperText="Use the UID stored on the user's Firebase Auth account."
      resetUserLabel="Reset user"
      resettingUserLabel="Resetting user..."
      isResettingAll={isResettingAll}
      isResettingUser={isResettingUser}
      errorMessage={errorMessage}
      successMessage={successMessage}
      onUserIdChange={setUserId}
      onResetAll={handleResetAll}
      onResetUser={handleResetUser}
    />
  )
}

export default WheelResetPanel
